import React, { useCallback, useEffect, useRef, useState } from "react"
import { Button, Col, Form, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { Page, InputField } from "@src/components"
import { GeoFenceMap } from "@src/settings/geofence/components/GeoFenceMap"
import { SearchCityFence } from "@src/settings/geofence/components/SearchCityFence"

type Coordinate = { lat: number; lng: number }

export type FleetGeofenceEditorProps = {
  header: string
  actionLabel: string
  initialName?: string
  initialCoordinates?: Coordinate[]
  onSubmit: (name: string, coordinates: Coordinate[]) => Promise<void>
  onSuccessNavigate?: () => void
}

export const FleetGeofenceEditor: React.FC<FleetGeofenceEditorProps> = ({
  header,
  actionLabel,
  initialName = "",
  initialCoordinates = [],
  onSubmit,
  onSuccessNavigate,
}) => {
  const { t } = useTranslation()
  const [fenceName, setFenceName] = useState<string>(initialName)
  const [fencingpost, setFencingpost] = useState<Coordinate[]>(initialCoordinates)
  const [mapCenter, setMapCenter] = useState<Coordinate>({ lat: 51.1657, lng: 10.4515 })
  const [mapZoom, setMapZoom] = useState<number>(5)
  const [loading, setLoading] = useState<boolean>(false)
  const [alertMsg, setAlertMsg] = useState<string>("")
  const searchCityFenceRef = useRef(null)

  const calculateZoomFromArea = (area: number) => {
    const ZOOM_LEVELS = [
      { area: 1, zoom: 20 },
      { area: 10, zoom: 19 },
      { area: 100, zoom: 18 },
      { area: 1000, zoom: 17 },
      { area: 10000, zoom: 16 },
      { area: 50000, zoom: 15 },
      { area: 100000, zoom: 14 },
      { area: 1000000, zoom: 13 },
      { area: 10000000, zoom: 12 },
      { area: 100000000, zoom: 11 },
      { area: 1000000000, zoom: 10 },
      { area: 10000000000, zoom: 7.5 },
      { area: 100000000000, zoom: 7 },
    ]
    for (let i = 0; i < ZOOM_LEVELS.length; i++) {
      if (area < ZOOM_LEVELS[i].area) {
        return ZOOM_LEVELS[i].zoom
      }
    }
    return 5
  }

  const findCenter = (coords: Coordinate[]) => {
    if (!coords.length) return
    let lat = 0
    let lng = 0
    for (let i = 0; i < coords.length; ++i) {
      lat += Number(coords[i].lat)
      lng += Number(coords[i].lng)
    }
    setMapCenter({ lat: lat / coords.length, lng: lng / coords.length })
  }

  const handleMapChange = useCallback((newCenter: Coordinate, newZoom: number) => {
    setMapCenter(newCenter)
    setMapZoom(newZoom)
  }, [])

  const handleSearchResult = useCallback((coords: Coordinate[]) => {
    setFencingpost(coords)
    if (coords.length > 0) {
      findCenter(coords)
      if (window.google && window.google.maps && window.google.maps.geometry) {
        const area = google.maps.geometry.spherical.computeArea(coords)
        setMapZoom(calculateZoomFromArea(area))
      }
    }
  }, [])

  // When initialCoordinates arrive later (e.g., edit screen), auto-center and zoom
  useEffect(() => {
    if (initialCoordinates && initialCoordinates.length > 0) {
      setFencingpost(initialCoordinates)
      findCenter(initialCoordinates)
      if (window.google && window.google.maps && window.google.maps.geometry) {
        const area = google.maps.geometry.spherical.computeArea(initialCoordinates)
        setMapZoom(calculateZoomFromArea(area))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCoordinates])

  const clearSearch = () => {
    if (searchCityFenceRef.current) {
      searchCityFenceRef.current.clearSearch()
    }
  }

  const handleSubmit = async () => {
    setAlertMsg("")
    if (fenceName.trim() === "") {
      setAlertMsg(t("settings.fence.validate.name"))
      return
    }
    if (fencingpost.length < 3) {
      setAlertMsg(t("settings.fence.validate.fencing_post"))
      return
    }
    setLoading(true)
    try {
      await onSubmit(fenceName, fencingpost)
      if (onSuccessNavigate) onSuccessNavigate()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Page isSettings showHeader headerValue={header}>
      <div className="d-flex flex-column-reverse flex-lg-row bg-highlight">
        <Col className="me-4">
          <GeoFenceMap
            center={mapCenter}
            zoom={mapZoom}
            geofence={fencingpost}
            onGeofenceChange={(fence) => {
              setFencingpost(fence)
            }}
            onMapChange={handleMapChange}
            editable
          />
          <Form.Text>{t("settings.fence.double_click")}</Form.Text>
        </Col>
        <Col xs={12} md={6} lg={4}>
          <InputField
            label={t("settings.fence.geofence_name")}
            value={fenceName}
            onPersist={(text) => setFenceName(text)}
            loading={false}
            onChangeText={() => undefined}
          />
          <Form.Text>{t("settings.fence.click_fence")}</Form.Text>
          <SearchCityFence
            ref={searchCityFenceRef}
            onSearchResult={(coords) => {
              handleSearchResult(coords)
            }}
          />
          <div className="d-flex mt-3">
            <Button
              className="me-2"
              variant="secondary"
              onClick={() => {
                setFencingpost([])
                setMapCenter({ lat: 51.1657, lng: 10.4515 })
                setMapZoom(5)
                clearSearch()
              }}
            >
              {t("settings.fence.clear_fence")}
            </Button>
            {loading ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <Button className="btn-primary-outline" onClick={handleSubmit}>
                {actionLabel}
              </Button>
            )}
          </div>
          {alertMsg && <div className="alert alert-danger mt-3">{alertMsg}</div>}
        </Col>
      </div>
    </Page>
  )
}

export default FleetGeofenceEditor
